import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiChevronUp, mdiUnfoldMoreHorizontal, mdiAlertOutline } from '@mdi/js'
import { useAuth, useIsPlatformAdmin } from '../../../hooks/useAuth'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { AppIcon } from '../../../components/ui/icon'
import { Badge } from '../../../components/ui/badge'
import { Input } from '../../../components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table'
import { EmptyState } from '../../../components/ui/empty-state'
import { UsageUserSessionsSheet } from '../../../components/admin/UsageUserSessionsSheet'
import { useAdminUsageUsers, type UsagePeriod } from '../../../hooks/use-admin-usage'

const SORT_FIELDS = ['user', 'organization', 'logins', 'api_calls', 'api_calls_machine', 'sessions_concurrent', 'suspicious'] as const
type SortField = (typeof SORT_FIELDS)[number]

interface UsageSearch {
  period?: UsagePeriod
  date?: string
  org?: string
  sort?: string
  user?: string
}

export const Route = createFileRoute('/admin/usage/')({
  validateSearch: (search: Record<string, unknown>): UsageSearch => {
    const out: UsageSearch = {}
    if (search.period === 'day' || search.period === 'month') out.period = search.period
    if (typeof search.date === 'string') out.date = search.date
    if (typeof search.org === 'string') out.org = search.org
    if (typeof search.sort === 'string') out.sort = search.sort
    if (typeof search.user === 'string') out.user = search.user
    return out
  },
  component: RouteComponent,
})

/** Today in UTC, as the usage buckets are UTC days. */
function todayUTC(): string {
  return new Date().toISOString().slice(0, 10)
}

function parseSort(sort: string | undefined): { field: SortField; dir: 'asc' | 'desc' } {
  const [field, dir] = (sort || 'suspicious:desc').split(':')
  const f = (SORT_FIELDS as readonly string[]).includes(field) ? (field as SortField) : 'suspicious'
  return { field: f, dir: dir === 'asc' ? 'asc' : 'desc' }
}

// Gated with useIsPlatformAdmin, like /admin/settings: a cross-org surface
// outside any $org context. The usage metrics are internal and never shown to
// organizations (spec 2026-10-01-02).
function RouteComponent() {
  const { t, i18n } = useTranslation('admin')
  const { loading, orgCode } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const navigate = useNavigate({ from: '/admin/usage/' })
  const search = Route.useSearch()

  useEffect(() => {
    if (!loading && !isPlatformAdmin) {
      navigate({ to: '/', replace: true })
    }
  }, [loading, isPlatformAdmin, navigate])

  const breadcrumbOrg = orgCode || 'demo'
  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${breadcrumbOrg}` },
    { title: t('settings:page.title'), path: `/${breadcrumbOrg}/settings` },
    { title: t('usage.title') },
  ])

  const period: UsagePeriod = search.period ?? 'day'
  const day = search.date || todayUTC()
  // The date input of a month reads YYYY-MM; the API accepts both.
  const date = period === 'month' ? day.slice(0, 7) : day
  const sort = parseSort(search.sort)

  const { data, isLoading, error } = useAdminUsageUsers({
    period,
    date,
    org: search.org,
    sort: `${sort.field}:${sort.dir}`,
  })
  const rows = data?.data ?? []

  const update = (patch: Partial<UsageSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const toggleSort = (field: SortField) => {
    const dir = sort.field === field
      ? (sort.dir === 'asc' ? 'desc' : 'asc')
      : (field === 'user' || field === 'organization' ? 'asc' : 'desc')
    update({ sort: `${field}:${dir}` })
  }

  // The organization filter is typed: only push it to the URL (and the query)
  // once typing pauses, so a half-typed code never flashes a 404.
  const [orgInput, setOrgInput] = useState(search.org ?? '')
  useEffect(() => {
    setOrgInput(search.org ?? '')
  }, [search.org])
  useEffect(() => {
    const value = orgInput.trim() || undefined
    if (value === search.org) return
    const timer = setTimeout(() => update({ org: value }), 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgInput])

  const numberFormat = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language])

  if (loading || !isPlatformAdmin) {
    return null
  }

  const sortableHead = (field: SortField, label: string, numeric = false) => (
    <TableHead
      className={numeric ? 'text-right' : undefined}
      aria-sort={sort.field === field ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
        data-testid={`usage-sort-${field}`}
      >
        {label}
        {sort.field !== field
          ? <AppIcon path={mdiUnfoldMoreHorizontal} size="xs" tone="muted" className="opacity-40" />
          : <AppIcon path={sort.dir === 'asc' ? mdiChevronUp : mdiChevronDown} size="xs" tone="muted" />}
      </button>
    </TableHead>
  )

  return (
    <div className="space-y-6" data-testid="admin-usage-page">
      <PageHeader
        icon={SECTION_ICONS.adminUsage}
        title={t('usage.title')}
        description={t('usage.subtitle')}
      />

      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex gap-4 items-center flex-wrap">
          <Tabs value={period} onValueChange={(v) => update({ period: v as UsagePeriod })}>
            <TabsList>
              <TabsTrigger value="day" data-testid="usage-period-day">{t('usage.period.day')}</TabsTrigger>
              <TabsTrigger value="month" data-testid="usage-period-month">{t('usage.period.month')}</TabsTrigger>
            </TabsList>
          </Tabs>
          <Input
            type={period === 'month' ? 'month' : 'date'}
            value={date}
            onChange={(e) => {
              const v = e.target.value
              if (!v) return
              update({ date: period === 'month' ? `${v}-01` : v })
            }}
            className="w-44"
            aria-label={t('usage.date')}
            data-testid="usage-date"
          />
          <Input
            value={orgInput}
            onChange={(e) => setOrgInput(e.target.value)}
            placeholder={t('usage.org_placeholder')}
            className="w-56"
            aria-label={t('usage.org_placeholder')}
            data-testid="usage-org"
          />
          <p className="text-xs text-muted-foreground flex-1 min-w-64">{t('usage.legend')}</p>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-destructive text-sm" data-testid="usage-error">
          {(error as { title?: string })?.title || t('usage.load_error')}
        </div>
      )}

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-4 text-center text-muted-foreground">{t('usage.loading')}</div>
        ) : rows.length === 0 ? (
          <EmptyState variant="inline" live className="py-8" title={t('usage.empty')} data-testid="usage-empty" />
        ) : (
          <Table data-testid="usage-table">
            <TableHeader>
              <TableRow>
                {sortableHead('user', t('usage.column.user'))}
                {sortableHead('organization', t('usage.column.organization'))}
                {sortableHead('logins', t('usage.column.logins'), true)}
                {sortableHead('api_calls', t('usage.column.api_calls'), true)}
                {sortableHead('api_calls_machine', t('usage.column.api_calls_machine'), true)}
                {sortableHead('sessions_concurrent', t('usage.column.sessions_concurrent'), true)}
                {sortableHead('suspicious', t('usage.column.status'))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={`${row.user_uid}-${row.organization_uid}`}
                  data-testid={`usage-row-${row.user_uid}-${row.organization_code}`}
                  data-suspicious={row.suspicious ? 'true' : 'false'}
                  className="cursor-pointer"
                  onClick={() => update({ user: row.user_uid })}
                >
                  <TableCell>
                    <div className="text-foreground">{row.user_name || row.user_email}</div>
                    {row.user_name && <div className="text-xs text-muted-foreground">{row.user_email}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-foreground">{row.organization_name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{row.organization_code}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{numberFormat.format(row.logins)}</TableCell>
                  <TableCell className="text-right tabular-nums">{numberFormat.format(row.api_calls)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{numberFormat.format(row.api_calls_machine)}</TableCell>
                  <TableCell className="text-right tabular-nums">{numberFormat.format(row.sessions_concurrent)}</TableCell>
                  <TableCell>
                    {row.suspicious ? (
                      <Badge variant="warning" data-testid={`usage-suspicious-${row.user_uid}-${row.organization_code}`}>
                        <AppIcon path={mdiAlertOutline} size="xs" className="mr-1" />
                        {t('usage.suspicious')}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('usage.normal')}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {rows.length > 0 && (
        <div className="text-sm text-muted-foreground" data-testid="usage-count">
          {t('usage.count', { count: data?.pagination.total ?? rows.length })}
        </div>
      )}

      <UsageUserSessionsSheet
        userUid={search.user}
        period={period}
        day={day}
        onClose={() => update({ user: undefined })}
      />
    </div>
  )
}
